import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Space,
  Spin,
  Switch,
  Tooltip,
  Typography,
  message,
} from 'antd'
import { CheckCircleFilled } from '@ant-design/icons'
import fetchAPI from '../../../lib/utils/fetch-api'

const { Text, Link: ExternalLink } = Typography

type Provider = 'mango' | 'avito' | 'calltouch'

type IntegrationState = {
  connected: boolean
  values: Record<string, string | boolean | null>
  secrets: Record<string, string | null>
}

type Overview = {
  head_office: { id: number; name: string | null } | null
  organization: { id: number; name: string | null } | null
  integrations: Record<Provider, IntegrationState>
}

type CheckResult = { ok: boolean; message: string; details?: Record<string, any> }

type FieldMeta = {
  name: string
  label: string
  secret?: boolean
  switch?: boolean
  required?: boolean
  placeholder?: string
}

type ProviderMeta = {
  key: Provider
  title: string
  target: 'office' | 'organization'
  hint: React.ReactNode
  fields: FieldMeta[]
}

/** Те же поля, что в карточке офиса (Mango, Calltouch) и настройках организации (Авито) */
const PROVIDERS: ProviderMeta[] = [
  {
    key: 'mango',
    title: 'Mango',
    target: 'office',
    hint: (
      <>Код АТС и ключ подписи — в личном кабинете Mango Office: «Интеграции» → «API коннектор».</>
    ),
    fields: [
      {
        name: 'mango_domain',
        label: 'Mango Office: домен',
        placeholder: 'vpbx000000000.mangosip.ru',
      },
      {
        name: 'mango_api',
        label: 'Mango Office: уникальный код АТС',
        secret: true,
        required: true,
      },
      {
        name: 'mango_salt',
        label: 'Mango Office: ключ для создания подписи',
        secret: true,
        required: true,
      },
      { name: 'mango_create_orders', label: 'Создавать обращения при звонках', switch: true },
    ],
  },
  {
    key: 'avito',
    title: 'Авито',
    target: 'organization',
    hint: (
      <>
        Номер профиля указан в{' '}
        <ExternalLink href="https://www.avito.ru/profile" target="_blank">
          личном кабинете
        </ExternalLink>
        , client_id и client_secret — на странице{' '}
        <ExternalLink href="https://www.avito.ru/professionals/api" target="_blank">
          «Интеграции»
        </ExternalLink>
        .
      </>
    ),
    fields: [
      { name: 'avito_account_id', label: 'Авито: номер профиля' },
      { name: 'avito_client_id', label: 'Авито: client_id', required: true },
      { name: 'avito_client_secret', label: 'Авито: client_secret', secret: true, required: true },
    ],
  },
  {
    key: 'calltouch',
    title: 'Calltouch',
    target: 'office',
    hint: <>Site ID и токен API — в личном кабинете Calltouch: «Интеграции» → «API».</>,
    fields: [
      { name: 'calltouch_site_id', label: 'Calltouch: site ID', required: true },
      { name: 'calltouch_token', label: 'Calltouch: токен API', secret: true, required: true },
    ],
  },
]

type Props = {
  /** Вызывается после сохранения (обновить статус шага) */
  onChanged?: () => void
}

export function HomeIntegrationsConnect({ onChanged }: Props) {
  const [overview, setOverview] = useState<Overview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [active, setActive] = useState<ProviderMeta | null>(null)
  const [checking, setChecking] = useState(false)
  const [saving, setSaving] = useState(false)
  const [checkResult, setCheckResult] = useState<CheckResult | null>(null)
  /** Сохранение доступно только после успешной проверки текущих значений полей */
  const canSave = Boolean(checkResult?.ok)
  const [form] = Form.useForm()

  const load = useCallback(async () => {
    setError(null)
    try {
      const res = await fetchAPI('/api/home/integrations')
      if (res?.errors) {
        setError(res.errors?.[0]?.detail || 'Не удалось загрузить интеграции')
        return
      }
      setOverview(res?.data || null)
    } catch (e: any) {
      setError(e?.message || 'Ошибка загрузки')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const open = (meta: ProviderMeta) => {
    const state = overview?.integrations?.[meta.key]
    const initial: Record<string, any> = {}
    for (const field of meta.fields) {
      if (field.secret) initial[field.name] = ''
      else if (field.switch) initial[field.name] = Boolean(state?.values?.[field.name])
      else initial[field.name] = state?.values?.[field.name] ?? ''
    }
    form.resetFields()
    form.setFieldsValue(initial)
    setCheckResult(null)
    setActive(meta)
  }

  const close = () => {
    setActive(null)
    setCheckResult(null)
  }

  const handleCheck = async () => {
    if (!active) return
    setChecking(true)
    setCheckResult(null)
    try {
      const snapshot = JSON.stringify(form.getFieldsValue(true))
      const res = await fetchAPI(`/api/home/integrations/${active.key}/check`, {
        method: 'POST',
        body: snapshot,
      })
      // Поля изменили, пока шла проверка — результат относится к старым значениям
      if (JSON.stringify(form.getFieldsValue(true)) !== snapshot) return
      if (res?.errors) {
        setCheckResult({ ok: false, message: res.errors?.[0]?.detail || 'Проверка не удалась' })
        return
      }
      const result: CheckResult = res?.data
      setCheckResult(result)
      // Авито сам сообщает номер профиля — подставим, если поле пустое
      const accountId = result?.details?.account_id
      if (active.key === 'avito' && accountId && !form.getFieldValue('avito_account_id')) {
        form.setFieldsValue({ avito_account_id: String(accountId) })
      }
    } catch (e: any) {
      setCheckResult({ ok: false, message: e?.message || 'Проверка не удалась' })
    } finally {
      setChecking(false)
    }
  }

  const handleSave = async () => {
    if (!active || !checkResult?.ok) return
    try {
      await form.validateFields()
    } catch {
      return
    }
    setSaving(true)
    try {
      const res = await fetchAPI(`/api/home/integrations/${active.key}`, {
        method: 'PUT',
        body: JSON.stringify(form.getFieldsValue(true)),
      })
      if (res?.errors) {
        const detail = res.errors?.[0]?.detail || 'Не удалось сохранить'
        // Сервер повторно проверил подключение и отклонил — показываем ошибку в модалке
        setCheckResult({ ok: false, message: detail })
        message.error(detail)
        return
      }
      message.success(`${active.title}: настройки сохранены`)
      close()
      await load()
      onChanged?.()
    } catch (e: any) {
      message.error(e?.message || 'Ошибка сохранения')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <Spin size="small" />
  if (error) return <Alert type="error" showIcon message={error} />

  const activeState = active ? overview?.integrations?.[active.key] : null
  const targetName = active
    ? active.target === 'office'
      ? overview?.head_office?.name || 'головной офис'
      : overview?.organization?.name || 'организация'
    : ''

  const renderButton = (meta: ProviderMeta) => {
    const connected = Boolean(overview?.integrations?.[meta.key]?.connected)
    return (
      <Tooltip
        key={meta.key}
        title={connected ? 'Подключено — нажмите, чтобы изменить' : undefined}
      >
        <Button
          type={connected ? 'default' : 'primary'}
          ghost={!connected}
          icon={connected ? <CheckCircleFilled style={{ color: '#52c41a' }} /> : undefined}
          onClick={() => open(meta)}
        >
          {connected ? `${meta.title} · Подключено` : `Подключить ${meta.title}`}
        </Button>
      </Tooltip>
    )
  }

  return (
    <>
      <Space wrap size={8}>
        {renderButton(PROVIDERS[0])}
        {renderButton(PROVIDERS[1])}
        <Tooltip title="Скоро">
          <span>
            <Button disabled>Подключить Яндекс Директ</Button>
          </span>
        </Tooltip>
        {renderButton(PROVIDERS[2])}
      </Space>

      <Modal
        open={Boolean(active)}
        title={active ? `Подключить ${active.title}` : ''}
        onCancel={close}
        destroyOnClose
        footer={[
          <Button key="check" onClick={handleCheck} loading={checking} disabled={saving}>
            Проверить подключение
          </Button>,
          <Button key="cancel" onClick={close} disabled={saving}>
            Отмена
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSave}
            loading={saving}
            disabled={!canSave || checking}
            title={canSave ? undefined : 'Сначала проверьте подключение'}
          >
            Сохранить
          </Button>,
        ]}
      >
        {active && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Text type="secondary">
              {active.hint} Настройки сохранятся в{' '}
              {active.target === 'office' ? 'головном офисе' : 'организации'} «{targetName}».
            </Text>
            <Form
              form={form}
              layout="vertical"
              autoComplete="off"
              requiredMark
              onValuesChange={() => setCheckResult(null)}
            >
              {active.fields.map(field => {
                const masked = field.secret ? activeState?.secrets?.[field.name] : null
                return (
                  <Form.Item
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    valuePropName={field.switch ? 'checked' : 'value'}
                    required={field.required}
                    extra={
                      masked ? `Сохранено: ${masked}. Оставьте пустым, чтобы не менять.` : undefined
                    }
                    rules={
                      field.required && !masked
                        ? [{ required: true, whitespace: true, message: 'Заполните поле' }]
                        : undefined
                    }
                  >
                    {field.switch ? (
                      <Switch />
                    ) : field.secret ? (
                      <Input.Password
                        placeholder={masked || field.placeholder}
                        autoComplete="new-password"
                        onChange={() => setCheckResult(null)}
                      />
                    ) : (
                      <Input
                        placeholder={field.placeholder}
                        onChange={() => setCheckResult(null)}
                      />
                    )}
                  </Form.Item>
                )
              })}
            </Form>
            {checkResult && (
              <Alert
                type={checkResult.ok ? 'success' : 'error'}
                showIcon
                message={checkResult.message}
              />
            )}
          </Space>
        )}
      </Modal>
    </>
  )
}

export default HomeIntegrationsConnect
